import { h, Component } from 'preact';
import util from 'preact-util';
import { Text } from 'preact-i18n';

// Article search/select for a single newsletter article block.
//
// Results are kept in LOCAL component state (not the shared `articleStore.searchResults`
// observable) so that multiple article pickers on the same page don't show each other's
// results — each instance searches and renders independently. On pick we clear the search
// field and the result list.
class ArticlePicker extends Component {
    constructor(props) {
        super(props);
        // Start in the "searching" state when we will preload (category-scoped AND no article
        // already chosen) so the preload doesn't flash the "no articles found" message before
        // results arrive. When a block already has a picked article (hasSelection), we do NOT
        // preload — see componentDidMount.
        this.state = { term: '', results: [], searching: !!props.category && !props.hasSelection };
        this.timer = null;
    }

    componentDidMount() {
        // When scoped to a category (e.g. the newsletter builder), preload that category's
        // articles so the list is visible without typing. doSearch() updates state async
        // (after the fetch), so this is not a synchronous did-mount setState.
        //
        // BUT skip the preload when the block already has a chosen article (hasSelection):
        // loading a saved draft would otherwise dump a 20-item result list under every
        // already-filled article block. The search field stays usable — typing reloads results.
        if (this.props.category && !this.props.hasSelection) {
            this.doSearch('');
        }
    }

    componentWillUnmount() {
        if (this.timer) {
            clearTimeout(this.timer);
        }
    }

    handleInput = (e) => {
        const term = e.target.value;
        this.setState({ term });
        if (this.timer) {
            clearTimeout(this.timer);
        }
        // Empty field: with a category, fall back to the full category list; otherwise clear.
        if (!term || term.trim().length === 0) {
            if (this.props.category) {
                this.setState({ searching: true });
                this.timer = setTimeout(() => this.doSearch(''), 300);
            } else {
                this.setState({ results: [], searching: false });
            }
            return;
        }
        this.setState({ searching: true });
        this.timer = setTimeout(() => this.doSearch(term), 300);
    }

    doSearch = async (term) => {
        const { articleStore } = this.props.stores;
        // Scope to the DOMAIN/hostname customer — newsletter & help articles live under the
        // hostname customer (the newsletter-admin page and help center both fetch them via
        // ArticleList, which always passes hostname). Without this, the backend scopes the
        // search to the logged-in/simulated user's own clinic customerIds, so a clinic admin
        // (e.g. simulating Eli, user 248) sees zero results because their clinic doesn't own
        // the newsletter/help library.
        const params = {
            hostname: articleStore.currentHostname,
            limit: 20,
            sort: '-publishDate -createdDate',
        };
        if (term) {
            params.search = term;
        }
        if (this.props.category) {
            params.category = this.props.category;
        }
        const response = await util.fetchApi('/api/articles/', { method: 'GET' }, params);
        // Ignore stale responses if the user kept typing (or cleared the field).
        if (this.state.term !== term) {
            return;
        }
        const results = response && Array.isArray(response.data) ? response.data : [];
        this.setState({ results, searching: false });
    }

    handlePick = (e) => {
        const { id } = e.currentTarget.dataset;
        const { onPick } = this.props;
        const article = this.state.results.find(a => String(a.id) === String(id));
        if (article && typeof onPick === 'function') {
            onPick(article);
        }
        // Empty the search field and results list after selecting.
        if (this.timer) {
            clearTimeout(this.timer);
        }
        this.setState({ term: '', results: [], searching: false });
    }

    render() {
        const { term, results, searching } = this.state;
        return (
            <div class='d-flex flex-column'>
                <input
                    type='text'
                    class='form-control rounded-pill mb-2'
                    value={term}
                    onInput={this.handleInput}
                    placeholder='Søk etter artikkel...'
                />
                {results.length > 0 && (
                    <div class='list-group'>
                        {results.slice(0, 20).map(a => (
                            <button
                                key={a.id}
                                type='button'
                                class='list-group-item list-group-item-action'
                                data-id={a.id}
                                onClick={this.handlePick}
                            >
                                <div class='d-flex flex-row justify-content-between align-items-start gap-2'>
                                    <strong>{a.title}</strong>
                                    {!!a.category && <span class='badge text-bg-secondary text-capitalize flex-shrink-0'>{a.category}</span>}
                                </div>
                                {!!a.ingress && <small class='d-block text-body-secondary text-truncate'>{a.ingress}</small>}
                            </button>
                        ))}
                    </div>
                )}
                {term.length > 2 && results.length === 0 && !searching && (
                    <small class='text-body-secondary'>
                        <Text id='newsletter.no-articles-found'>No articles found.</Text>
                    </small>
                )}
            </div>
        );
    }
}

export default ArticlePicker;
